import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  NativeSelect,
  Card,
  Badge,
  Flex,
  IconButton,
  SimpleGrid,
  useDisclosure,
  Icon,
  Spinner,
  Table,
  Textarea,
  ButtonGroup,
  Tabs,
  Alert,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { toast } from '@/components/ui/toaster';
import { Thead, Tbody, Tr, Th, Td, Divider, TabList, Tab, TabPanels, TabPanel, Switch, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaDrum, FaPhone, FaEnvelope, FaFilter, FaSave, FaTimes, FaSort,
  FaCamera, FaLayerGroup, FaDownload, FaFilePdf, FaFileExcel, FaInfoCircle,
} from 'react-icons/fa';
import { musicosAPI, instrumentosAPI, bandasAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { downloadMusicosListadoExcel, downloadMusicosListadoPdf } from '../utils/exportMusicosListado';
import { useProductTour } from '../hooks/useProductTour';
import { bandaMusicosSteps } from '../tours/bandaMusicosTour';
import {
  formatMusicoInstrumentosText,
  musicoFamiliasSet,
  primeraFamiliaMusico,
} from '../utils/musicoInstrumentos';
import { Tooltip } from '@/components/ui/tooltip';

const FAMILIA_LABELS = {
  VIENTO_METAL: 'Viento Metal',
  VIENTO_MADERA: 'Viento Madera',
  PERCUSION: 'Percusión',
  CUERDA: 'Cuerda',
  VOZ: 'Voz',
  OTRO: 'Otro',
};

const FAMILIA_COLORS = {
  VIENTO_METAL: 'yellow',
  VIENTO_MADERA: 'green',
  PERCUSION: 'red',
  CUERDA: 'blue',
  VOZ: 'purple',
  OTRO: 'gray',
};

function newRowKey() {
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const MAX_MUSICO_FOTO_BYTES = 5 * 1024 * 1024;

function hasInstrumentoAsignado(row) {
  return Boolean(row?.instrumentoId || row?.instrumentoPersonalizadoId);
}

function listaTieneInstrumentoValido(rows) {
  return Array.isArray(rows) && rows.some(hasInstrumentoAsignado);
}

function validateMusicoFotoFile(file) {
  if (!file.type.startsWith('image/')) return 'El archivo debe ser una imagen (JPG, PNG, WebP…).';
  if (file.size > MAX_MUSICO_FOTO_BYTES) return 'La imagen no puede superar 5 MB.';
  return null;
}

export default function MusicosTab({ toast, tipoGrupo }) {
  const { profile } = useAuth();
  useProductTour('banda-musicos', bandaMusicosSteps, { autoStart: false });
  const [musicos, setMusicos] = useState([]);
  const [instrumentos, setInstrumentos] = useState([]);
  const [instrumentosPersonalizados, setInstrumentosPersonalizados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [musicosTab, setMusicosTab] = useState(0); // 0: Plantilla, 1: Grupos
  const [search, setSearch] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState('');
  const [activoFilter, setActivoFilter] = useState('activo'); // 'todos' | 'activo' | 'inactivo'
  const [sortBy, setSortBy] = useState('nombre_asc');
  const toolbarBg = useColorModeValue('gray.50', 'gray.750');
  const borderSubtle = useColorModeValue('gray.200', 'gray.600');
  const [editingMusico, setEditingMusico] = useState(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const { open: isVozOpen, onOpen: onVozOpen, onClose: onVozClose } = useDisclosure();
  const { open: isAddInstOpen, onOpen: onAddInstOpen, onClose: onAddInstClose } = useDisclosure();
  const { open: isNuevoInstOpen, onOpen: onNuevoInstOpen, onClose: onNuevoInstClose } = useDisclosure();
  const {
    open: isDeleteMusicoOpen,
    onOpen: onDeleteMusicoOpen,
    onClose: onDeleteMusicoClose,
  } = useDisclosure();
  const {
    open: isEstadoModalOpen,
    onOpen: onEstadoModalOpen,
    onClose: onEstadoModalClose,
  } = useDisclosure();
  const cancelDeleteMusicoRef = useRef(null);
  const cancelEstadoRef = useRef(null);
  const [musicoPendienteEliminar, setMusicoPendienteEliminar] = useState(null);
  const [deletingMusico, setDeletingMusico] = useState(false);
  /** Cambio de activo pendiente: listado o formulario de edición. */
  const [estadoPendiente, setEstadoPendiente] = useState(null);
  const [togglingEstado, setTogglingEstado] = useState(false);
  const [newInst, setNewInst] = useState({ nombre: '', familia: 'OTRO' });
  const [instPersonalizadoSaving, setInstPersonalizadoSaving] = useState(false);
  const [vocesCatalog, setVocesCatalog] = useState([]);
  const [nuevaVozNombre, setNuevaVozNombre] = useState('');
  const [vocesSaving, setVocesSaving] = useState(false);
  const [listaInstrumentos, setListaInstrumentos] = useState([]);
  const [addInstSelect, setAddInstSelect] = useState('');
  const [addInstVoz, setAddInstVoz] = useState('');
  const [addInstEditKey, setAddInstEditKey] = useState(null);
  const cardBg = useColorModeValue('white', 'gray.800');
  const fotoInputRef = useRef(null);
  /** Archivo elegido pendiente de subir al guardar el músico. */
  const [pendingFotoFile, setPendingFotoFile] = useState(null);
  /** Tras guardar, si había foto en servidor y el usuario la quitó (solo edición). */
  const [fotoRemoved, setFotoRemoved] = useState(false);
  /** URL para vista previa (`blob:` o URL del servidor). */
  const [fotoDisplaySrc, setFotoDisplaySrc] = useState(null);
  const [savingMusico, setSavingMusico] = useState(false);
  const [instrumentosError, setInstrumentosError] = useState(false);
  const { open: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();
  const [exportLoading, setExportLoading] = useState(false);

  const [form, setForm] = useState({
    nombre: '', apellidos: '', email: '', telefono: '', notas: '', activo: true,
  });

  // ─── Grupos de músicos ───────────────────────────────────────────
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const {
    open: isGrupoModalOpen,
    onOpen: onGrupoModalOpen,
    onClose: onGrupoModalClose,
  } = useDisclosure();
  const {
    open: isDeleteGrupoOpen,
    onOpen: onDeleteGrupoOpen,
    onClose: onDeleteGrupoClose,
  } = useDisclosure();
  const cancelDeleteGrupoRef = useRef(null);
  const [editingGrupo, setEditingGrupo] = useState(null);
  const [grupoPendienteEliminar, setGrupoPendienteEliminar] = useState(null);
  const [deletingGrupo, setDeletingGrupo] = useState(false);
  const [savingGrupo, setSavingGrupo] = useState(false);
  const [grupoForm, setGrupoForm] = useState({ nombre: '', descripcion: '' });
  const [grupoMiembrosSeleccionados, setGrupoMiembrosSeleccionados] = useState([]);

  const fetchGrupos = useCallback(async () => {
    try {
      setLoadingGrupos(true);
      const res = await musicosAPI.getGrupos();
      setGrupos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingGrupos(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const instParams = tipoGrupo?.trim() ? { tipoGrupo: tipoGrupo.trim() } : {};
      const [mRes, iRes, ipRes, vRes] = await Promise.all([
        musicosAPI.getAll(),
        instrumentosAPI.getAll(instParams),
        instrumentosAPI.getPersonalizados(),
        bandasAPI.listPartiturasVoces().catch(() => ({ data: [] })),
      ]);
      setMusicos(mRes.data);
      setInstrumentos(iRes.data);
      setInstrumentosPersonalizados(ipRes.data);
      setVocesCatalog(Array.isArray(vRes.data) ? vRes.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tipoGrupo]);

  useEffect(() => { fetchData(); fetchGrupos(); }, [fetchData, fetchGrupos]);

  const allInstruments = [
    ...instrumentos.map((i) => ({ ...i, type: 'catalog' })),
    ...instrumentosPersonalizados.map((i) => ({ ...i, type: 'custom' })),
  ];

  const revokeFotoPreviewIfBlob = useCallback(() => {
    setFotoDisplaySrc((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
  }, []);

  const closeMusicoModal = () => {
    setListaInstrumentos([]);
    setAddInstSelect('');
    setAddInstVoz('');
    setAddInstEditKey(null);
    setPendingFotoFile(null);
    setFotoRemoved(false);
    revokeFotoPreviewIfBlob();
    setFotoDisplaySrc(null);
    setInstrumentosError(false);
    onClose();
  };

  const handleOpenNew = () => {
    setEditingMusico(null);
    setInstrumentosError(false);
    setForm({
      nombre: '', apellidos: '', email: '', telefono: '', notas: '', activo: true,
    });
    setListaInstrumentos([]);
    setPendingFotoFile(null);
    setFotoRemoved(false);
    revokeFotoPreviewIfBlob();
    setFotoDisplaySrc(null);
    onOpen();
  };

  const handleOpenEdit = (musico) => {
    setEditingMusico(musico);
    setInstrumentosError(false);
    setPendingFotoFile(null);
    setFotoRemoved(false);
    setFotoDisplaySrc(musico.foto || null);
    setForm({
      nombre: musico.nombre,
      apellidos: musico.apellidos || '',
      email: musico.email || '',
      telefono: musico.telefono || '',
      notas: musico.notas || '',
      activo: musico.activo,
    });
    const rows = (musico.instrumentosAsignados || []).map((r) => ({
      key: r.id || newRowKey(),
      instrumentoId: r.instrumentoId || '',
      instrumentoPersonalizadoId: r.instrumentoPersonalizadoId || '',
      vozPartituraPersonalizadaId: r.vozPartituraPersonalizadaId || r.vozPartituraPersonalizada?.id || '',
    }));
    setListaInstrumentos(rows);
    onOpen();
  };

  const handleFotoFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const err = validateMusicoFotoFile(file);
    if (err) {
      toast({ title: err, status: 'warning' });
      return;
    }
    setFotoDisplaySrc((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return URL.createObjectURL(file);
    });
    setPendingFotoFile(file);
    setFotoRemoved(false);
  };

  const handleClearFoto = () => {
    setPendingFotoFile(null);
    setFotoDisplaySrc((prev) => {
      if (prev && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
    if (editingMusico?.foto) setFotoRemoved(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning' });
      return;
    }
    if (!listaTieneInstrumentoValido(listaInstrumentos)) {
      setInstrumentosError(true);
      toast({
        title: 'El instrumento es obligatorio',
        description: 'Añade al menos un instrumento al músico antes de guardar.',
        status: 'warning',
      });
      return;
    }
    setInstrumentosError(false);
    try {
      setSavingMusico(true);
      const data = {
        nombre: form.nombre.trim(),
        apellidos: form.apellidos.trim() || null,
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
        notas: form.notas.trim() || null,
        activo: form.activo,
        instrumentosAsignados: listaInstrumentos.map((r) => ({
          instrumentoId: r.instrumentoId || null,
          instrumentoPersonalizadoId: r.instrumentoPersonalizadoId || null,
          vozPartituraPersonalizadaId: r.vozPartituraPersonalizadaId || null,
        })),
      };
      let musicoId = editingMusico?.id;
      if (editingMusico) {
        await musicosAPI.update(editingMusico.id, data);
        toast({ title: 'Músico actualizado', status: 'success' });
      } else {
        const res = await musicosAPI.create(data);
        musicoId = res.data?.id;
        toast({ title: 'Músico añadido', status: 'success' });
      }
      if (pendingFotoFile && musicoId) {
        await musicosAPI.uploadFoto(musicoId, pendingFotoFile);
      } else if (editingMusico && fotoRemoved && musicoId) {
        await musicosAPI.deleteFoto(musicoId);
      }
      closeMusicoModal();
      fetchData();
    } catch (err) {
      toast({
        title: 'Error al guardar',
        description: err.response?.data?.message,
        status: 'error',
      });
    } finally {
      setSavingMusico(false);
    }
  };

  const handleAddVozCatalogo = async () => {
    const n = nuevaVozNombre.trim();
    if (!n) {
      toast({ title: 'Escribe un nombre de voz', status: 'warning' });
      return;
    }
    try {
      setVocesSaving(true);
      const res = await bandasAPI.createPartituraVoz({ nombre: n });
      const list = Array.isArray(res.data) ? res.data : [];
      setVocesCatalog(list);
      const created = list.find((v) => (v.nombre || '').trim() === n);
      if (created?.id) setAddInstVoz(created.id);
      setNuevaVozNombre('');
      onVozClose();
      toast({ title: 'Voz añadida al catálogo', status: 'success' });
    } catch (err) {
      toast({
        title: 'No se pudo crear la voz',
        description: err.response?.data?.message,
        status: 'error',
      });
    } finally {
      setVocesSaving(false);
    }
  };

  const handleCreateInstrumentoPersonalizado = async () => {
    if (!newInst.nombre.trim()) {
      toast({ title: 'Escribe un nombre de instrumento', status: 'warning' });
      return;
    }
    try {
      setInstPersonalizadoSaving(true);
      const res = await instrumentosAPI.createPersonalizado(newInst);
      const created = res.data;
      await fetchData();
      if (created?.id) {
        setAddInstSelect(`custom:${created.id}`);
      }
      setNewInst({ nombre: '', familia: 'OTRO' });
      onNuevoInstClose();
      toast({ title: 'Instrumento añadido al catálogo', status: 'success' });
    } catch (err) {
      toast({
        title: 'No se pudo crear el instrumento',
        description: err.response?.data?.message,
        status: 'error',
      });
    } finally {
      setInstPersonalizadoSaving(false);
    }
  };

  const requestDeleteMusico = (m) => {
    setMusicoPendienteEliminar(m);
    onDeleteMusicoOpen();
  };

  const confirmDeleteMusico = async () => {
    if (!musicoPendienteEliminar?.id) return;
    try {
      setDeletingMusico(true);
      await musicosAPI.delete(musicoPendienteEliminar.id);
      toast({ title: 'Músico eliminado', status: 'info' });
      await fetchData();
    } catch (err) {
      toast({ title: 'Error al eliminar', status: 'error' });
    } finally {
      setDeletingMusico(false);
      setMusicoPendienteEliminar(null);
      onDeleteMusicoClose();
    }
  };

  const requestToggleEstado = (musico) => {
    if (!musico?.id) return;
    setEstadoPendiente({
      source: 'list',
      musico,
      nextActivo: !musico.activo,
    });
    onEstadoModalOpen();
  };

  const requestFormActivoChange = (nextActivo) => {
    if (Boolean(form.activo) === Boolean(nextActivo)) return;
    setEstadoPendiente({
      source: 'form',
      musico: editingMusico
        ? {
            id: editingMusico.id,
            nombre: form.nombre || editingMusico.nombre,
            apellidos: form.apellidos ?? editingMusico.apellidos,
          }
        : { nombre: form.nombre, apellidos: form.apellidos },
      nextActivo: Boolean(nextActivo),
    });
    onEstadoModalOpen();
  };

  const closeEstadoModal = () => {
    if (togglingEstado) return;
    setEstadoPendiente(null);
    onEstadoModalClose();
  };

  const confirmEstadoChange = async () => {
    if (!estadoPendiente) return;
    const { source, musico, nextActivo } = estadoPendiente;
    if (source === 'form') {
      setForm((f) => ({ ...f, activo: nextActivo }));
      setEstadoPendiente(null);
      onEstadoModalClose();
      return;
    }
    if (!musico?.id) return;
    setTogglingEstado(true);
    try {
      await musicosAPI.update(musico.id, { activo: nextActivo });
      toast({
        title: nextActivo ? 'Músico reactivado' : 'Músico marcado como inactivo',
        status: 'success',
      });
      await fetchData();
      setEstadoPendiente(null);
      onEstadoModalClose();
    } catch (err) {
      toast({ title: 'Error al cambiar el estado', status: 'error' });
    } finally {
      setTogglingEstado(false);
    }
  };

  const handleToggleActive = (musico) => {
    requestToggleEstado(musico);
  };

  const grouped = {};
  Object.keys(FAMILIA_LABELS).forEach((f) => { grouped[f] = []; });
  allInstruments.forEach((i) => {
    if (grouped[i.familia]) grouped[i.familia].push(i);
  });

  const openAddInstrumentoModal = () => {
    setAddInstEditKey(null);
    setAddInstSelect('');
    setAddInstVoz('');
    onAddInstOpen();
  };

  const openEditInstrumentoModal = (row) => {
    setAddInstEditKey(row.key);
    if (row.instrumentoId) setAddInstSelect(`catalog:${row.instrumentoId}`);
    else if (row.instrumentoPersonalizadoId) setAddInstSelect(`custom:${row.instrumentoPersonalizadoId}`);
    else setAddInstSelect('');
    setAddInstVoz(row.vozPartituraPersonalizadaId || '');
    onAddInstOpen();
  };

  const applyAddInstrumento = () => {
    if (!addInstSelect) {
      toast({ title: 'Elige un instrumento', status: 'warning' });
      return;
    }
    const [type, id] = addInstSelect.split(':');
    const row = {
      key: addInstEditKey || newRowKey(),
      instrumentoId: type === 'catalog' ? id : '',
      instrumentoPersonalizadoId: type === 'custom' ? id : '',
      vozPartituraPersonalizadaId: addInstVoz || '',
    };
    if (addInstEditKey) {
      setListaInstrumentos((prev) =>
        prev.map((r) => (r.key === addInstEditKey ? { ...row, key: addInstEditKey } : r)),
      );
    } else {
      setListaInstrumentos((prev) => [...prev, row]);
    }
    setInstrumentosError(false);
    onAddInstClose();
    setAddInstSelect('');
    setAddInstVoz('');
    setAddInstEditKey(null);
  };

  const removeInstrumentoRow = (key) => {
    setListaInstrumentos((prev) => {
      const next = prev.filter((r) => r.key !== key);
      if (listaTieneInstrumentoValido(next)) setInstrumentosError(false);
      return next;
    });
  };

  const labelOneRow = (row) => {
    const inst = allInstruments.find(
      (i) =>
        (i.type === 'catalog' && i.id === row.instrumentoId) ||
        (i.type === 'custom' && i.id === row.instrumentoPersonalizadoId),
    );
    const n = inst ? `${inst.nombre}${inst.type === 'custom' ? ' (pers.)' : ''}` : '?';
    const v = vocesCatalog.find((x) => x.id === row.vozPartituraPersonalizadaId);
    return v?.nombre ? `${n} — ${v.nombre}` : n;
  };

  // ─── Handlers de grupos ──────────────────────────────────────────

  const openNewGrupo = () => {
    setEditingGrupo(null);
    setGrupoForm({ nombre: '', descripcion: '' });
    setGrupoMiembrosSeleccionados([]);
    onGrupoModalOpen();
  };

  const openEditGrupo = (grupo) => {
    setEditingGrupo(grupo);
    setGrupoForm({
      nombre: grupo.nombre,
      descripcion: grupo.descripcion || '',
    });
    setGrupoMiembrosSeleccionados(
      (grupo.miembros || []).map((m) => m.musico?.id || m.musicoId).filter(Boolean),
    );
    onGrupoModalOpen();
  };

  const closeGrupoModal = () => {
    setEditingGrupo(null);
    setGrupoForm({ nombre: '', descripcion: '' });
    setGrupoMiembrosSeleccionados([]);
    onGrupoModalClose();
  };

  const handleSaveGrupo = async () => {
    if (!grupoForm.nombre.trim()) {
      toast({ title: 'El nombre del grupo de músicos es obligatorio', status: 'warning' });
      return;
    }
    try {
      setSavingGrupo(true);
      const payload = {
        nombre: grupoForm.nombre.trim(),
        descripcion: grupoForm.descripcion.trim() || null,
        musicoIds: grupoMiembrosSeleccionados,
      };
      if (editingGrupo) {
        await musicosAPI.updateGrupo(editingGrupo.id, payload);
        toast({ title: 'Grupo de músicos actualizado', status: 'success' });
      } else {
        await musicosAPI.createGrupo(payload);
        toast({ title: 'Grupo de músicos creado', status: 'success' });
      }
      closeGrupoModal();
      fetchGrupos();
    } catch (err) {
      toast({
        title: 'Error al guardar grupo de músicos',
        description: err.response?.data?.message,
        status: 'error',
      });
    } finally {
      setSavingGrupo(false);
    }
  };

  const requestDeleteGrupo = (g) => {
    setGrupoPendienteEliminar(g);
    onDeleteGrupoOpen();
  };

  const confirmDeleteGrupo = async () => {
    if (!grupoPendienteEliminar?.id) return;
    try {
      setDeletingGrupo(true);
      await musicosAPI.deleteGrupo(grupoPendienteEliminar.id);
      toast({ title: 'Grupo de músicos eliminado', status: 'info' });
      await fetchGrupos();
    } catch (err) {
      toast({ title: 'Error al eliminar grupo de músicos', status: 'error' });
    } finally {
      setDeletingGrupo(false);
      setGrupoPendienteEliminar(null);
      onDeleteGrupoClose();
    }
  };

  const toggleGrupoMiembro = (musicoId) => {
    setGrupoMiembrosSeleccionados((prev) =>
      prev.includes(musicoId)
        ? prev.filter((id) => id !== musicoId)
        : [...prev, musicoId],
    );
  };

  const filtersActive = Boolean(search || familiaFilter || activoFilter !== 'activo' || sortBy !== 'nombre_asc');

  const clearFilters = () => {
    setSearch('');
    setFamiliaFilter('');
    setActivoFilter('activo');
    setSortBy('nombre_asc');
  };

  const filtered = musicos
    .filter((m) => {
      const hay = (s) => (s || '').toLowerCase().includes(search.toLowerCase());
      const instText = formatMusicoInstrumentosText(m, ' ');
      const matchSearch =
        !search ||
        hay(`${m.nombre} ${m.apellidos || ''}`) ||
        hay(instText);
      const fams = musicoFamiliasSet(m);
      const matchFamilia = !familiaFilter || fams.has(familiaFilter);
      const matchActivo =
        activoFilter === 'todos' ? true :
        activoFilter === 'inactivo' ? !m.activo :
        m.activo;
      return matchSearch && matchFamilia && matchActivo;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'nombre_desc':
          return `${b.nombre} ${b.apellidos || ''}`.localeCompare(`${a.nombre} ${a.apellidos || ''}`, 'es');
        case 'familia': {
          const fa = primeraFamiliaMusico(a) || 'ZZZ';
          const fb = primeraFamiliaMusico(b) || 'ZZZ';
          const cmp = fa.localeCompare(fb, 'es');
          if (cmp !== 0) return cmp;
          return `${a.nombre} ${a.apellidos || ''}`.localeCompare(`${b.nombre} ${b.apellidos || ''}`, 'es');
        }
        case 'activo_first':
          if (a.activo !== b.activo) return a.activo ? -1 : 1;
          return `${a.nombre} ${a.apellidos || ''}`.localeCompare(`${b.nombre} ${b.apellidos || ''}`, 'es');
        case 'nombre_asc':
        default:
          return `${a.nombre} ${a.apellidos || ''}`.localeCompare(`${b.nombre} ${b.apellidos || ''}`, 'es');
      }
    });

  const buildExportSubtitle = () => {
    const parts = [];
    if (search.trim()) parts.push(`Búsqueda: «${search.trim()}»`);
    if (familiaFilter) parts.push(`Familia: ${FAMILIA_LABELS[familiaFilter] || familiaFilter}`);
    if (activoFilter === 'activo') parts.push('Solo activos');
    else if (activoFilter === 'inactivo') parts.push('Solo inactivos');
    else if (activoFilter === 'todos') parts.push('Todos los estados');
    parts.push(`${filtered.length} músico${filtered.length === 1 ? '' : 's'}`);
    parts.push(
      new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }),
    );
    return parts.join(' · ');
  };

  const handleExportListado = async (format) => {
    if (!filtered.length) {
      toast({ title: 'No hay músicos para exportar', status: 'info' });
      return;
    }
    try {
      setExportLoading(true);
      const bandName = profile?.nombre || 'Mi banda';
      const subtitle = buildExportSubtitle();
      if (format === 'pdf') {
        await downloadMusicosListadoPdf({ musicos: filtered, bandName, subtitle });
      } else {
        await downloadMusicosListadoExcel({ musicos: filtered, bandName, subtitle });
      }
      toast({ title: 'Listado descargado', status: 'success' });
      onExportClose();
    } catch (e) {
      toast({
        title: 'No se pudo generar el archivo',
        description: e?.message || 'Inténtalo de nuevo.',
        status: 'error',
        duration: 6000,
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return <Flex justify="center" py={12}><Spinner size="xl" color="purple.500" /></Flex>;
  }

  const hayMusicos = musicos.length > 0;

  return (
    <VStack gap={6} align="stretch">
      <Tabs.Root
        colorPalette="purple"
        variant='enclosed'
        value={tabsControlledValue(musicosTab)}
        onValueChange={tabsOnValueChange(setMusicosTab)}
      >
        <Tabs.List flexWrap="wrap" data-tour="musicos-tabs">
          <Tab value="0" fontWeight="600">
            <Icon mr={2} asChild><FaDrum /></Icon> Plantilla de músicos
            <Badge colorPalette="purple" ml={2} fontSize="xs">{musicos.length}</Badge>
          </Tab>
          <Tab value="1" fontWeight="600">
            <Icon mr={2} asChild><FaLayerGroup /></Icon> Grupos de músicos
            <Badge colorPalette="purple" ml={2} fontSize="xs">{grupos.length}</Badge>
          </Tab>
        </Tabs.List>

        <TabPanels>
          {/* ═══ Pestaña: Plantilla de músicos ═══ */}
          <TabPanel value="0" px={0} pt={4}>
      <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
        <Card.Body>
          <HStack align="center" mb={4} gap={3} flexWrap="wrap" rowGap={2}>
            <Heading size="md" flexShrink={0}>
              Plantilla de músicos
            </Heading>
            <Button
              size="sm"
              variant="outline"
              colorPalette="purple"
              onClick={onExportOpen}
              flexShrink={0}
              disabled={filtered.length === 0}><FaDownload />Descargar listado
                          </Button>
            <Button
              size="sm"
              colorPalette="purple"
              onClick={handleOpenNew}
              flexShrink={0}
              data-tour="musicos-add"><FaPlus />Añadir músico
                          </Button>
          </HStack>

          {/* ── Barra de filtros ── */}
          <Box
            bg={toolbarBg}
            borderWidth="1px"
            borderColor={borderSubtle}
            rounded="xl"
            p={3}
            mb={4}
            data-tour="musicos-filtros"
          >
            <Flex direction={{ base: 'column', md: 'row' }} gap={3} align={{ md: 'flex-end' }} wrap="wrap">
              <InputGroup size="sm" flex="1" minW="180px" maxW="280px">
                <InputLeftElement><Icon color="gray.400" asChild><FaSearch /></Icon></InputLeftElement>
                <Input
                  placeholder="Buscar nombre o instrumento…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  rounded="lg"
                />
              </InputGroup>
              <Field.Root w={{ base: 'full', md: 'auto' }} minW="160px" maxW="200px">
                <Field.Label fontSize="xs" mb={1} fontWeight="600">Familia</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    size="sm"
                    rounded="lg"
                    placeholder="Todas las familias"
                    value={familiaFilter}
                    onChange={(e) => setFamiliaFilter(e.target.value)}>
                    {Object.entries(FAMILIA_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root w={{ base: 'full', md: 'auto' }} minW="170px" maxW="210px">
                <Field.Label fontSize="xs" mb={1} fontWeight="600">Ordenar por</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    size="sm"
                    rounded="lg"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}>
                    <option value="nombre_asc">Nombre (A → Z)</option>
                    <option value="nombre_desc">Nombre (Z → A)</option>
                    <option value="familia">Familia de instrumento</option>
                    <option value="activo_first">Activos primero</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root w={{ base: 'full', md: 'auto' }}>
                <Field.Label fontSize="xs" mb={1} fontWeight="600">Estado</Field.Label>
                <ButtonGroup size="sm" attached variant="outline">
                  <Button
                    colorPalette={activoFilter === 'activo' ? 'purple' : 'gray'}
                    variant={activoFilter === 'activo' ? 'solid' : 'outline'}
                    onClick={() => setActivoFilter('activo')}
                  >
                    Activos
                  </Button>
                  <Button
                    colorPalette={activoFilter === 'inactivo' ? 'gray' : 'gray'}
                    variant={activoFilter === 'inactivo' ? 'solid' : 'outline'}
                    onClick={() => setActivoFilter('inactivo')}
                  >
                    Inactivos
                  </Button>
                  <Button
                    colorPalette={activoFilter === 'todos' ? 'gray' : 'gray'}
                    variant={activoFilter === 'todos' ? 'solid' : 'outline'}
                    onClick={() => setActivoFilter('todos')}
                  >
                    Todos
                  </Button>
                </ButtonGroup>
              </Field.Root>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                disabled={!filtersActive}
                alignSelf={{ md: 'flex-end' }}
                flexShrink={0}><FaTimes />Limpiar
                              </Button>
            </Flex>
            <Text fontSize="xs" color="gray.500" mt={2}>
              Mostrando {filtered.length} de {musicos.length} {musicos.length === 1 ? 'músico' : 'músicos'}
            </Text>
          </Box>

          <Alert.Root status="info" variant="subtle" rounded="lg" mb={4}>
            <Alert.Indicator />
            <Box>
              <Alert.Title fontSize="sm">¿Para qué sirve el estado?</Alert.Title>
              <Alert.Description fontSize="xs" display="block">
                <strong>Activo</strong>: el músico forma parte de la plantilla operativa (ensayos, formaciones,
                actuaciones, encuestas y repertorio). <strong>Inactivo</strong>: se conserva en el censo pero
                deja de convocarse y de aparecer en esas gestiones. No elimina sus datos; puedes reactivarlo
                cuando quieras. Por defecto el listado muestra solo activos.
              </Alert.Description>
            </Box>
          </Alert.Root>

          {filtered.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>
              {musicos.length === 0 ? 'Aún no has añadido músicos a tu plantilla.' : 'No se encontraron músicos con esos filtros.'}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="simple">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader w="56px"></Table.ColumnHeader>
                    <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                    <Table.ColumnHeader>Instrumentos / voces</Table.ColumnHeader>
                    <Table.ColumnHeader display={{ base: 'none', md: 'table-cell' }}>Contacto</Table.ColumnHeader>
                    <Table.ColumnHeader>
                      <HStack gap={1} align="center">
                        <Text as="span">Estado</Text>
                        <Tooltip
                          content="Activo = en plantilla operativa (ensayos, formaciones, actuaciones, encuestas). Inactivo = se conserva pero no se convoca. Al cambiarlo te pediremos confirmación."
                          showArrow
                          positioning={{ placement: 'top' }}
                        >
                          <Box as="span" display="inline-flex" color="gray.500" cursor="help" lineHeight={1}>
                            <Icon as={FaInfoCircle} boxSize={3.5} />
                          </Box>
                        </Tooltip>
                      </HStack>
                    </Table.ColumnHeader>
                    <Table.ColumnHeader></Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filtered.map((m) => {
                    const fam = primeraFamiliaMusico(m);
                    return (
                      <Table.Row key={m.id} opacity={m.activo ? 1 : 0.5}>
                        <Table.Cell py={2} verticalAlign="middle">
                          <Avatar.Root size="sm"><Avatar.Fallback name={`${m.nombre} ${m.apellidos || ''}`} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                        </Table.Cell>
                        <Table.Cell fontWeight="500">{m.nombre} {m.apellidos || ''}</Table.Cell>
                        <Table.Cell>
                          {fam && (
                            <Badge colorPalette={FAMILIA_COLORS[fam] || 'gray'} mr={1} fontSize="xs">
                              {FAMILIA_LABELS[fam] || ''}
                            </Badge>
                          )}
                          <Text as="span" fontSize="sm">{formatMusicoInstrumentosText(m)}</Text>
                        </Table.Cell>
                        <Table.Cell display={{ base: 'none', md: 'table-cell' }}>
                          <Text fontSize="xs" color="gray.600">{m.telefono || m.email || '-'}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={2}>
                            <Badge
                              colorPalette={m.activo ? 'green' : 'gray'}
                              variant="subtle"
                              fontSize="xs"
                              rounded="full"
                            >
                              {m.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                            <Switch
                              size="sm"
                              checked={m.activo}
                              onValueChange={() => handleToggleActive(m)}
                              colorPalette="green"
                              aria-label={m.activo ? 'Marcar como inactivo' : 'Reactivar músico'}
                            />
                          </HStack>
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={1}>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              onClick={() => handleOpenEdit(m)}
                              aria-label="Editar"><FaEdit /></IconButton>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={() => requestDeleteMusico(m)}
                              aria-label="Eliminar"><FaTrash /></IconButton>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>
          </TabPanel>

          {/* ═══ Pestaña: Grupos de músicos ═══ */}
          <TabPanel value="1" px={0} pt={4}>
      <Card.Root bg={cardBg} shadow="lg" rounded="2xl">
        <Card.Body>
          <HStack align="center" mb={4} gap={3} flexWrap="wrap" rowGap={2}>
            <Heading size="md" flexShrink={0}>
              Grupos de músicos
            </Heading>
            <Box flex={1} />
            <Button
              size="sm"
              colorPalette="purple"
              onClick={openNewGrupo}
              flexShrink={0}
              disabled={!hayMusicos}><FaPlus />Crear grupo de músicos
                          </Button>
          </HStack>

          {!hayMusicos ? (
            <Alert.Root status="info" borderRadius="md">
              <Alert.Indicator />
              <Box>
                <Text fontWeight="600" fontSize="sm">Primero añade músicos a la plantilla</Text>
                <Text fontSize="sm" mt={1}>
                  Para crear grupos de músicos necesitas tener al menos un músico dado de alta.
                  Ve a la pestaña «Plantilla de músicos» y añade a tus músicos.
                </Text>
              </Box>
            </Alert.Root>
          ) : (
            <>
          <Text fontSize="xs" color="gray.500" mb={4}>
            Organiza a tus músicos en secciones o grupos (p. ej. «Trompetas», «Percusión», «Junta directiva»).
          </Text>

          {loadingGrupos ? (
            <Flex justify="center" py={6}><Spinner size="md" color="purple.500" /></Flex>
          ) : grupos.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={6} fontSize="sm">
              Aún no has creado ningún grupo de músicos. Pulsa «Crear grupo de músicos» para empezar.
            </Text>
          ) : (
            <VStack align="stretch" gap={3}>
              {grupos.map((g) => (
                <Box
                  key={g.id}
                  borderWidth="1px"
                  borderColor={borderSubtle}
                  rounded="xl"
                  p={4}
                >
                  <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                    <Box flex={1} minW={0}>
                      <HStack gap={2} mb={1}>
                        <Text fontWeight="600" fontSize="md">{g.nombre}</Text>
                        <Badge colorPalette="gray" fontSize="xs">
                          {g.miembros?.length || 0} {(g.miembros?.length || 0) === 1 ? 'músico' : 'músicos'}
                        </Badge>
                      </HStack>
                      {g.descripcion && (
                        <Text fontSize="sm" color="gray.600" mb={2}>{g.descripcion}</Text>
                      )}
                      {g.miembros?.length > 0 && (
                        <Flex gap={2} flexWrap="wrap">
                          {g.miembros.map((mb) => (
                            <HStack
                              key={mb.musico?.id || mb.id}
                              gap={1.5}
                              bg="gray.50"
                              _dark={{ bg: 'gray.700' }}
                              rounded="lg"
                              px={2}
                              py={1}
                              align="center"
                            >
                              <Avatar.Root size="2xs"><Avatar.Fallback name={`${mb.musico?.nombre || ''} ${mb.musico?.apellidos || ''}`.trim()} /><Avatar.Image src={mb.musico?.foto || undefined} /></Avatar.Root>
                              <Box>
                                <Text fontSize="xs" lineHeight="short">
                                  {mb.musico?.nombre || ''} {mb.musico?.apellidos || ''}
                                  {mb.musico && !mb.musico.activo ? ' (inactivo)' : ''}
                                </Text>
                                {mb.musico && formatMusicoInstrumentosText(mb.musico) && (
                                  <Text fontSize="0.65rem" color="gray.500" lineHeight="short" lineClamp={1}>
                                    {formatMusicoInstrumentosText(mb.musico)}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          ))}
                        </Flex>
                      )}
                    </Box>
                    <HStack gap={1} flexShrink={0}>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => openEditGrupo(g)}
                        aria-label="Editar grupo de músicos"><FaEdit /></IconButton>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => requestDeleteGrupo(g)}
                        aria-label="Eliminar grupo de músicos"><FaTrash /></IconButton>
                    </HStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
            </>
          )}
        </Card.Body>
      </Card.Root>
          </TabPanel>
        </TabPanels>
      </Tabs.Root>

      {/* ─── Modal crear/editar grupo ─── */}
      <Dialog.Root open={isGrupoModalOpen} size='lg' onOpenChange={e => {
        if (!e.open) {
          closeGrupoModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{editingGrupo ? 'Editar grupo de músicos' : 'Crear grupo de músicos'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Nombre del grupo de músicos</Field.Label>
                    <Input
                      size="sm"
                      placeholder="Ej. Trompetas, Percusión, Junta directiva…"
                      value={grupoForm.nombre}
                      onChange={(e) => setGrupoForm((f) => ({ ...f, nombre: e.target.value }))}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Descripción (opcional)</Field.Label>
                    <Textarea
                      size="sm"
                      rows={2}
                      placeholder="Breve descripción del grupo de músicos…"
                      value={grupoForm.descripcion}
                      onChange={(e) => setGrupoForm((f) => ({ ...f, descripcion: e.target.value }))}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Músicos del grupo</Field.Label>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Selecciona los músicos que pertenecen a este grupo de músicos. Un músico puede estar en varios grupos.
                    </Text>
                    {musicos.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" py={2}>No hay músicos en la plantilla todavía.</Text>
                    ) : (
                      <Box
                        maxH="250px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderColor={borderSubtle}
                        rounded="lg"
                        p={2}
                      >
                        <VStack align="stretch" gap={1}>
                          {musicos
                            .slice()
                            .sort((a, b) =>
                              `${a.nombre} ${a.apellidos || ''}`.localeCompare(
                                `${b.nombre} ${b.apellidos || ''}`,
                                'es',
                              ),
                            )
                            .map((m) => {
                              const sel = grupoMiembrosSeleccionados.includes(m.id);
                              return (
                                <HStack
                                  key={m.id}
                                  gap={3}
                                  px={2}
                                  py={1.5}
                                  rounded="md"
                                  cursor="pointer"
                                  bg={sel ? 'purple.50' : 'transparent'}
                                  _dark={sel ? { bg: 'purple.900' } : {}}
                                  _hover={{ bg: sel ? 'purple.100' : 'gray.50', _dark: { bg: sel ? 'purple.800' : 'gray.700' } }}
                                  onClick={() => toggleGrupoMiembro(m.id)}
                                  opacity={m.activo ? 1 : 0.55}
                                >
                                  <Switch
                                    size="sm"
                                    checked={sel}
                                    onValueChange={() => toggleGrupoMiembro(m.id)}
                                    colorPalette="purple"
                                    pointerEvents="none"
                                  />
                                  <Avatar.Root size="xs"><Avatar.Fallback name={`${m.nombre} ${m.apellidos || ''}`.trim()} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                                  <Text fontSize="sm" flex={1}>
                                    {m.nombre} {m.apellidos || ''}
                                    {!m.activo ? ' (inactivo)' : ''}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                    {formatMusicoInstrumentosText(m)}
                                  </Text>
                                </HStack>
                              );
                            })}
                        </VStack>
                      </Box>
                    )}
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {grupoMiembrosSeleccionados.length} seleccionado{grupoMiembrosSeleccionados.length === 1 ? '' : 's'}
                    </Text>
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={closeGrupoModal} disabled={savingGrupo}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSaveGrupo} loading={savingGrupo}><FaSave />{editingGrupo ? 'Guardar' : 'Crear'}</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ─── Confirmar eliminar grupo ─── */}
      <Dialog.Root
        open={isDeleteGrupoOpen}
        initialFocusEl={() => cancelDeleteGrupoRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            if (deletingGrupo) return;
            setGrupoPendienteEliminar(null);
            onDeleteGrupoClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar grupo de músicos
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.700">
                    ¿Seguro que quieres eliminar el grupo de músicos{' '}
                    <strong>{grupoPendienteEliminar?.nombre || ''}</strong>?
                    Los músicos no se eliminarán, solo se deshace la agrupación.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelDeleteGrupoRef}
                    onClick={() => {
                      setGrupoPendienteEliminar(null);
                      onDeleteGrupoClose();
                    }}
                    disabled={deletingGrupo}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteGrupo} loading={deletingGrupo}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root open={isOpen} size='lg' onOpenChange={e => {
        if (!e.open) {
          closeMusicoModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{editingMusico ? 'Editar músico' : 'Añadir músico'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Field.Root>
                    <Field.Label fontSize="sm">Foto de perfil (opcional)</Field.Label>
                    <HStack gap={4} align="flex-start" flexWrap="wrap">
                      <Avatar.Root size="lg" bg="purple.100" color="purple.700"><Avatar.Fallback name={`${form.nombre} ${form.apellidos || ''}`.trim() || '?'} /><Avatar.Image src={fotoDisplaySrc || undefined} /></Avatar.Root>
                      <VStack align="start" gap={2}>
                        <input
                          ref={fotoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          hidden
                          onChange={handleFotoFileChange}
                        />
                        <HStack gap={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="purple"
                            onClick={() => fotoInputRef.current?.click()}><FaCamera />{fotoDisplaySrc || pendingFotoFile ? 'Cambiar foto' : 'Subir foto'}</Button>
                          {(fotoDisplaySrc || pendingFotoFile) && (
                            <Button size="sm" variant="ghost" colorPalette="red" onClick={handleClearFoto}><FaTimes />Quitar
                                                      </Button>
                          )}
                        </HStack>
                        <Field.HelperText fontSize="xs" mb={0} maxW="md">
                          Máx. 5 MB. JPEG.
                        </Field.HelperText>
                      </VStack>
                    </HStack>
                  </Field.Root>

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
                    <Field.Root required>
                      <Field.Label fontSize="sm">Nombre</Field.Label>
                      <Input size="sm" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label fontSize="sm">Apellidos</Field.Label>
                      <Input size="sm" value={form.apellidos} onChange={(e) => setForm((f) => ({ ...f, apellidos: e.target.value }))} />
                    </Field.Root>
                  </SimpleGrid>

                  <Field.Root required invalid={instrumentosError}>
                    <Field.Label fontSize="sm">Instrumento</Field.Label>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Añade al menos un instrumento (con voz opcional del catálogo de la banda). Un músico puede tener varios.
                    </Text>
                    {listaInstrumentos.length === 0 ? (
                      <Text fontSize="sm" color={instrumentosError ? 'red.500' : 'gray.500'} py={2}>
                        Ninguno aún. Pulsa «Añadir instrumento…».
                      </Text>
                    ) : (
                      <VStack align="stretch" gap={2} mb={2}>
                        {listaInstrumentos.map((row) => (
                          <HStack key={row.key} bg="gray.50" _dark={{ bg: 'gray.700' }} rounded="md" px={3} py={2} justify="space-between">
                            <Text fontSize="sm" flex={1}>{labelOneRow(row)}</Text>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              aria-label="Editar línea"
                              onClick={() => openEditInstrumentoModal(row)}><FaEdit /></IconButton>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              aria-label="Quitar"
                              onClick={() => removeInstrumentoRow(row.key)}><FaTimes /></IconButton>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    <Button size="sm" variant="outline" onClick={openAddInstrumentoModal}><FaPlus />Añadir instrumento…
                                      </Button>
                    {instrumentosError ? (
                      <Field.HelperText color="red.500" fontSize="xs">
                        Debes asignar al menos un instrumento.
                      </Field.HelperText>
                    ) : null}
                  </Field.Root>

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
                    <Field.Root>
                      <Field.Label fontSize="sm">Teléfono</Field.Label>
                      <Input size="sm" value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label fontSize="sm">Email</Field.Label>
                      <Input size="sm" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                    </Field.Root>
                  </SimpleGrid>
                  <Field.Root>
                    <Field.Label fontSize="sm">Notas</Field.Label>
                    <Textarea size="sm" value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} rows={2} />
                  </Field.Root>
                  {editingMusico && (
                    <Field.Root>
                      <HStack justify="space-between" align="center" w="full" flexWrap="wrap" gap={2}>
                        <Box>
                          <Field.Label fontSize="sm" mb={0}>Estado</Field.Label>
                          <Text fontSize="xs" color="gray.500">
                            Activo = plantilla operativa · Inactivo = no se convoca (se conserva el registro)
                          </Text>
                        </Box>
                        <HStack gap={2}>
                          <Badge colorPalette={form.activo ? 'green' : 'gray'} variant="subtle" rounded="full">
                            {form.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                          <Switch
                            checked={form.activo}
                            onCheckedChange={(details) => {
                              const next = details?.checked === true;
                              requestFormActivoChange(next);
                            }}
                            colorPalette="green"
                          />
                        </HStack>
                      </HStack>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={closeMusicoModal} disabled={savingMusico}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSave} loading={savingMusico}><FaSave />{editingMusico ? 'Guardar' : 'Añadir'}</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={isAddInstOpen} size='md' onOpenChange={e => {
        if (!e.open)
          { onAddInstClose(); setAddInstEditKey(null); }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{addInstEditKey ? 'Editar instrumento' : 'Añadir instrumento'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  <Field.Root required>
                    <Field.Label fontSize="sm">Instrumento</Field.Label>
                    <HStack align="flex-start" gap={2}>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          size="sm"
                          flex={1}
                          placeholder="Elige instrumento…"
                          value={addInstSelect}
                          onChange={(e) => setAddInstSelect(e.target.value)}>
                          {Object.entries(grouped).map(([familia, items]) =>
                            items.length > 0 && (
                              <optgroup key={familia} label={FAMILIA_LABELS[familia]}>
                                {items.map((i) => (
                                  <option key={`${i.type}:${i.id}`} value={`${i.type}:${i.id}`}>
                                    {i.nombre}{i.type === 'custom' ? ' (personalizado)' : ''}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <Button size="sm" variant="outline" flexShrink={0} onClick={onNuevoInstOpen}>
                        Nuevo instrumento…
                      </Button>
                    </HStack>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Voz (opcional)</Field.Label>
                    <HStack align="flex-start" gap={2}>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          size="sm"
                          flex={1}
                          placeholder="Sin voz"
                          value={addInstVoz}
                          onChange={(e) => setAddInstVoz(e.target.value)}>
                          {vocesCatalog.map((v) => (
                            <option key={v.id} value={v.id}>{v.nombre}</option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <Button size="sm" variant="outline" flexShrink={0} onClick={onVozOpen}>Nueva voz…</Button>
                    </HStack>
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={() => { onAddInstClose(); setAddInstEditKey(null); }}>Cancelar</Button>
                <Button colorPalette="purple" onClick={applyAddInstrumento}>Aceptar</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isNuevoInstOpen}
        size='sm'
        onOpenChange={e => {
          if (!e.open) {
            onNuevoInstClose();
            setNewInst({ nombre: '', familia: 'OTRO' });
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Nuevo instrumento personalizado</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Se guardará en el catálogo de tu banda (músicos, formaciones y repertorio).
                </Text>
                <VStack gap={4}>
                  <Field.Root required>
                    <Field.Label fontSize="sm">Nombre</Field.Label>
                    <Input
                      size="sm"
                      value={newInst.nombre}
                      onChange={(e) => setNewInst((i) => ({ ...i, nombre: e.target.value }))}
                      placeholder="Ej. Gaita gallega"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Familia</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={newInst.familia}
                        onChange={(e) => setNewInst((i) => ({ ...i, familia: e.target.value }))}>
                        {Object.entries(FAMILIA_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    onNuevoInstClose();
                    setNewInst({ nombre: '', familia: 'OTRO' });
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleCreateInstrumentoPersonalizado}
                  loading={instPersonalizadoSaving}
                >
                  Crear instrumento
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isVozOpen}
        size='sm'
        onOpenChange={e => {
          if (!e.open) {
            onVozClose();
            setNuevaVozNombre('');
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Nueva voz en el catálogo</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Se guardará para tu banda (repertorio y músicos).
                </Text>
                <Field.Root>
                  <Field.Label fontSize="sm">Nombre</Field.Label>
                  <Input
                    size="sm"
                    placeholder="Ej. 1.ª, Soprano, Tenor…"
                    value={nuevaVozNombre}
                    onChange={(e) => setNuevaVozNombre(e.target.value)}
                    maxLength={120}
                  />
                </Field.Root>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={() => { onVozClose(); setNuevaVozNombre(''); }}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleAddVozCatalogo} loading={vocesSaving}>Crear voz</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={isExportOpen} placement='center' size='sm' onOpenChange={e => {
        if (!e.open) {
          onExportClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md" py={3}>Descargar listado</Dialog.Header>
              <Dialog.CloseTrigger disabled={exportLoading} size="sm" />
              <Dialog.Body pb={4} pt={0}>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Se exportará el listado actual ({filtered.length} músico{filtered.length === 1 ? '' : 's'}
                  {search.trim() || familiaFilter || activoFilter !== 'activo' ? ', con los filtros aplicados' : ''}
                  ).
                </Text>
                <SimpleGrid columns={2} gap={3}>
                  <Button
                    size="sm"
                    h="auto"
                    py={3}
                    px={2}
                    flexDirection="column"
                    variant="outline"
                    colorPalette="red"
                    onClick={() => handleExportListado('pdf')}
                    loading={exportLoading}
                    loadingText="Generando…"
                  >
                    <Icon boxSize={4} mb={1} asChild><FaFilePdf /></Icon>
                    PDF
                    <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                      Con cabecera Cadencya
                    </Text>
                  </Button>
                  <Button
                    size="sm"
                    h="auto"
                    py={3}
                    px={2}
                    flexDirection="column"
                    variant="outline"
                    colorPalette="green"
                    onClick={() => handleExportListado('excel')}
                    loading={exportLoading}
                    loadingText="Generando…"
                  >
                    <Icon boxSize={4} mb={1} asChild><FaFileExcel /></Icon>
                    Excel
                    <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                      Hoja de cálculo .xlsx
                    </Text>
                  </Button>
                </SimpleGrid>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isDeleteMusicoOpen}
        initialFocusEl={() => cancelDeleteMusicoRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            if (deletingMusico) return;
            setMusicoPendienteEliminar(null);
            onDeleteMusicoClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar músico
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.700">
                    ¿Seguro que quieres eliminar a{' '}
                    <strong>
                      {musicoPendienteEliminar
                        ? `${musicoPendienteEliminar.nombre} ${musicoPendienteEliminar.apellidos || ''}`.trim()
                        : 'este músico'}
                    </strong>
                    ? Esta acción no se puede deshacer.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelDeleteMusicoRef}
                    onClick={() => {
                      setMusicoPendienteEliminar(null);
                      onDeleteMusicoClose();
                    }}
                    disabled={deletingMusico}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteMusico} loading={deletingMusico}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <Dialog.Root
        open={isEstadoModalOpen}
        initialFocusEl={() => cancelEstadoRef.current}
        role="alertdialog"
        placement="center"
        onOpenChange={(e) => {
          if (!e.open) closeEstadoModal();
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                {estadoPendiente?.nextActivo ? 'Reactivar músico' : 'Marcar músico como inactivo'}
              </Dialog.Header>
              <Dialog.Body>
                <VStack align="stretch" gap={3}>
                  <Text fontSize="sm" color="gray.700">
                    Vas a cambiar el estado de{' '}
                    <strong>
                      {estadoPendiente?.musico
                        ? `${estadoPendiente.musico.nombre || ''} ${estadoPendiente.musico.apellidos || ''}`.trim() ||
                          'este músico'
                        : 'este músico'}
                    </strong>
                    .
                  </Text>
                  {estadoPendiente?.nextActivo ? (
                    <Alert.Root status="success" variant="subtle" rounded="lg">
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">Al reactivarlo</Alert.Title>
                        <Alert.Description fontSize="xs" display="block">
                          Volverá a la plantilla operativa: podrá convocarse a ensayos, formaciones, actuaciones y
                          encuestas, y aparecerá de nuevo en las gestiones de la banda.
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                  ) : (
                    <Alert.Root status="warning" variant="subtle" rounded="lg">
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">Al marcarlo inactivo</Alert.Title>
                        <Alert.Description fontSize="xs" display="block" as="div">
                          <Text mb={2}>
                            Dejará de formar parte de la plantilla operativa. En la práctica:
                          </Text>
                          <Box as="ul" pl={4} style={{ listStyle: 'disc' }}>
                            <li>No se incluirá en nuevas convocatorias de ensayos ni encuestas</li>
                            <li>No aparecerá para asignar en formaciones ni actuaciones</li>
                            <li>No se elimina: se conserva el historial y podrás reactivarlo cuando quieras</li>
                          </Box>
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                  )}
                  {estadoPendiente?.source === 'form' ? (
                    <Text fontSize="xs" color="gray.500">
                      El cambio se aplicará al guardar la ficha del músico.
                    </Text>
                  ) : null}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={3}>
                <Button ref={cancelEstadoRef} variant="ghost" onClick={closeEstadoModal} disabled={togglingEstado}>
                  Cancelar
                </Button>
                <Button
                  colorPalette={estadoPendiente?.nextActivo ? 'green' : 'orange'}
                  onClick={confirmEstadoChange}
                  loading={togglingEstado}
                >
                  {estadoPendiente?.nextActivo ? 'Sí, reactivar' : 'Sí, marcar inactivo'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

    </VStack>
  );
}
